import * as Progress from '@radix-ui/react-progress';
import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';

import { composedModel } from '@/entities/composed';
import { formatStorageSize, organizationModel } from '@/entities/organization';

import { storageLimitMessages } from '../../config/i18n';

import styles from './StorageLimit.module.scss';

type StorageLimitProps = {
  className?: string;
};

export const StorageLimit: FC<StorageLimitProps> = (props) => {
  const { className } = props;

  const { formatMessage } = useIntl();

  const isStorageFeatureReady = useAppSelector(
    composedModel.selectors.selectIsStorageFeatureReady,
  );

  const storageUsed = useAppSelector(
    organizationModel.selectors.selectStorageUsed,
  );

  const storageLimit = useAppSelector(
    composedModel.selectors.selectTotalStorageLimit,
  );

  const percentage = useAppSelector(
    composedModel.selectors.selectStoragePercentage,
  );

  const isWarning = useAppSelector(
    composedModel.selectors.selectIsStorageWarning,
  );

  const isFull = useAppSelector(composedModel.selectors.selectIsStorageFull);

  if (!isStorageFeatureReady) {
    return null;
  }

  const getStatusClassName = (prefix: 'progress' | 'used') => {
    if (isFull) {
      return styles[`${prefix}Full`];
    }

    if (isWarning) {
      return styles[`${prefix}Warning`];
    }

    return prefix === 'progress' ? styles.progressNormal : styles.used;
  };

  const formattedUsed = formatStorageSize(storageUsed);
  const formattedLimit = formatStorageSize(storageLimit);

  return (
    <div
      className={cn(styles.container, className)}
      data-component="storage-limit"
    >
      <h4 className="h4">{formatMessage(storageLimitMessages.title)}</h4>

      <div className={styles.storageRow}>
        <div className={cn(styles.storageLabel, 'p2')}>
          {formatMessage(storageLimitMessages.storageLabel)}
        </div>

        <div className={styles.progressContainer}>
          <Progress.Root className={styles.progressRoot} value={100}>
            <Progress.Indicator
              className={cn(
                styles.progressIndicator,
                getStatusClassName('progress'),
              )}
              style={{
                transform: `translateX(-${100 - Math.min(percentage, 100)}%)`,
              }}
            />
          </Progress.Root>

          <div className={cn(styles.limits, 'p3')}>
            <FormattedMessage
              {...storageLimitMessages.usedOutOfLimit}
              values={{
                used: (
                  <span className={getStatusClassName('used')}>
                    {formattedUsed}
                  </span>
                ),
                limit: formattedLimit,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
